import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Card, Typography, Grid, TextField, Button, Divider, Chip, Checkbox, FormControlLabel, Stack, IconButton, InputAdornment, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Alert, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArticleIcon from '@mui/icons-material/Article';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  border: '1px solid #eef2f6',
  marginBottom: '24px',
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: '#2563eb',
  marginBottom: 20,
  fontSize: '1.1rem',
}));

const RolesContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
  gap: 8,
}));

const UserView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { state } = useLocation();
  const userFromState = state?.user || {};
  const userId = params.id || userFromState?.id;

  const [basic, setBasic] = useState({
    email: userFromState?.email || '',
    username: userFromState?.username || userFromState?.email || '',
    first_name: userFromState?.first_name || userFromState?.name?.split?.(' ')?.[0] || '',
    last_name: userFromState?.last_name || ''
  });
  const [status, setStatus] = useState(() => {
    const s = userFromState?.status;
    if (typeof s === 'boolean') return s;
    if (typeof s === 'number') return s === 1;
    if (typeof s === 'string') return ['true', 'active', 'enabled', '1', 'yes'].includes(s.toLowerCase());
    return false;
  });
  const [passwords, setPasswords] = useState({ new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ new: false, confirm: false });
  const [saving, setSaving] = useState({ basic: false, password: false, status: false, roles: false });
  const [roles, setRoles] = useState(() => {
    // Example available roles
    return {
      SalesManager: false,
      CRO: false,
      Account: false,
      BDM: false,
      HR: false,
      SalesExecutive: false,
      Admin: false,
      SEO: false,
      CROManager: false,
      AccountManager: false,
      Telecaller: false,
      Basic: false,
    };
  });

  const roleNames = useMemo(() => Object.keys(roles), [roles]);

  // Monthly Plan State
  const [monthlyPlanData, setMonthlyPlanData] = useState({
    posts: [],
    gmbAccounts: [],
    loading: true,
    error: null,
    user: null
  });
  const [deletingPostId, setDeletingPostId] = useState(null);

  // Fetch user's monthly plan data on mount
  useEffect(() => {
    const fetchMonthlyPlanData = async () => {
      if (!userId) return;

      try {
        logger.log(`📋 [UserView] Fetching monthly plan data for user ${userId}`);
        const response = await ApiService.get(`/admin/gmb-account/user-posts/${userId}`);
        const data = response?.data || response;

        logger.log(`📋 [UserView] Monthly plan data:`, data);

        setMonthlyPlanData({
          posts: data.posts || [],
          gmbAccounts: data.gmbAccounts || [],
          user: data.user || null,
          loading: false,
          error: null
        });

        // Update basic info with fetched user data if not already set
        if (data.user && !basic.email) {
          setBasic(prev => ({
            ...prev,
            email: data.user.email || prev.email,
            first_name: data.user.name?.split(' ')[0] || prev.first_name
          }));
        }
      } catch (error) {
        logger.error('❌ [UserView] Error fetching monthly plan data:', error);
        setMonthlyPlanData(prev => ({
          ...prev,
          loading: false,
          error: error?.message || 'Failed to load monthly plan data'
        }));
      }
    };

    fetchMonthlyPlanData();
  }, [userId]);

  // Handle delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled post?')) return;

    setDeletingPostId(postId);
    try {
      await ApiService.delete(`/admin/gmb-account/user-post/${postId}`);
      setMonthlyPlanData(prev => ({
        ...prev,
        posts: prev.posts.filter(p => p.id !== postId)
      }));
      logger.log(`✅ [UserView] Post ${postId} deleted successfully`);
    } catch (error) {
      logger.error('❌ [UserView] Error deleting post:', error);
      alert('Failed to delete post: ' + (error?.message || 'Unknown error'));
    } finally {
      setDeletingPostId(null);
    }
  };

  // Format date for display
  const formatScheduledDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get post type display
  const getPostTypeChip = (post) => {
    let descData = {};
    try {
      descData = post.description ? JSON.parse(post.description) : {};
    } catch (e) {
      // ignore
    }

    const templateType = descData.template_type;
    const topicType = descData.topicType || 'STANDARD';

    if (templateType === 'festival-edm' || descData.festival_name) {
      return <Chip label="🎉 Festival" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />;
    }
    if (templateType === 'testimonial' || topicType === 'TESTIMONIAL') {
      return <Chip label="⭐ Review" size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }} />;
    }
    if (templateType === 'gmb-photo' || topicType === 'PHOTO') {
      return <Chip label="📸 Photo" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }} />;
    }
    if (templateType === 'ai-generated' || topicType === 'STANDARD') {
      return <Chip label="🤖 AI" size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 600 }} />;
    }
    return <Chip label={topicType} size="small" />;
  };

  // Check if post is already posted
  const isPosted = (post) => {
    return !!post.posted_at;
  };

  const handleBasicUpdate = async () => {
    setSaving(s => ({ ...s, basic: true }));
    try {
      // await ApiService.put(`/admin/users/${userId}`, basic);
    } finally {
      setSaving(s => ({ ...s, basic: false }));
    }
  };

  const handlePasswordUpdate = async () => {
    if (!passwords.new_password || passwords.new_password.length < 8) return;
    if (passwords.new_password !== passwords.confirm_password) return;
    setSaving(s => ({ ...s, password: true }));
    try {
      // await ApiService.post(`/admin/users/${userId}/password`, { password: passwords.new_password });
      setPasswords({ new_password: '', confirm_password: '' });
    } finally {
      setSaving(s => ({ ...s, password: false }));
    }
  };

  const handleStatusToggle = async (next) => {
    setStatus(next);
    setSaving(s => ({ ...s, status: true }));
    try {
      // await ApiService.post(`/admin/users/${userId}/status`, { active: next });
    } finally {
      setSaving(s => ({ ...s, status: false }));
    }
  };

  const handleRolesUpdate = async () => {
    setSaving(s => ({ ...s, roles: true }));
    try {
      const enabled = Object.entries(roles).filter(([k, v]) => v).map(([k]) => k);
      // await ApiService.post(`/admin/users/${userId}/roles`, { roles: enabled });
    } finally {
      setSaving(s => ({ ...s, roles: false }));
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, color: '#374151' }}>Back</Typography>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>User Form</Typography>

      <SectionCard>
        <SectionHeader variant="subtitle1">Basic Info</SectionHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Email Address" value={basic.email} onChange={e => setBasic({ ...basic, email: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="User Name" value={basic.username} onChange={e => setBasic({ ...basic, username: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="First Name" value={basic.first_name} onChange={e => setBasic({ ...basic, first_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Last Name" value={basic.last_name} onChange={e => setBasic({ ...basic, last_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleBasicUpdate}
                disabled={saving.basic}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 160,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
                }}
              >
                {saving.basic ? 'Updating...' : 'Update'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </SectionCard>

      <Divider sx={{ my: 3 }} />

      <SectionCard>
        <SectionHeader variant="subtitle1">Password</SectionHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="New Password" type={show.new ? 'text' : 'password'}
              value={passwords.new_password} onChange={e => setPasswords({ ...passwords, new_password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(s => ({ ...s, new: !s.new }))} edge="end">
                      {show.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" label="Confirm Password" type={show.confirm ? 'text' : 'password'}
              value={passwords.confirm_password} onChange={e => setPasswords({ ...passwords, confirm_password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))} edge="end">
                      {show.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handlePasswordUpdate}
                disabled={saving.password}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 160,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
                }}
              >
                {saving.password ? 'Updating...' : 'Update'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </SectionCard>

      <Divider sx={{ my: 3 }} />

      <SectionCard>
        <SectionHeader variant="subtitle1">Status</SectionHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2">User Status : {status ? 'ACTIVE' : 'DISABLE'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Chip label="DISABLE" color="default" variant={status ? 'outlined' : 'filled'} onClick={() => handleStatusToggle(false)} sx={{ fontWeight: 600 }} />
              <Chip label="ACTIVE" color="primary" variant={status ? 'filled' : 'outlined'} onClick={() => handleStatusToggle(true)} sx={{ fontWeight: 600 }} />
            </Stack>
          </Grid>
        </Grid>
      </SectionCard>

      <Divider sx={{ my: 3 }} />

      <SectionCard>
        <SectionHeader variant="subtitle1">Roles</SectionHeader>
        <RolesContainer>
          {roleNames.map((r) => (
            <FormControlLabel
              key={r}
              control={<Checkbox checked={roles[r]} onChange={(_, checked) => setRoles(prev => ({ ...prev, [r]: checked }))} />}
              label={r}
            />
          ))}
        </RolesContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleRolesUpdate}
            disabled={saving.roles}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 160,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
            }}
          >
            {saving.roles ? 'Updating...' : 'Update'}
          </Button>
        </Box>
      </SectionCard>

      <Divider sx={{ my: 3 }} />

      {/* Monthly Plan Section */}
      <SectionCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarMonthIcon sx={{ color: '#2563eb' }} />
          <SectionHeader variant="subtitle1" sx={{ mb: 0 }}>Monthly Plan</SectionHeader>
        </Box>

        {/* GMB Accounts Overview */}
        {monthlyPlanData.gmbAccounts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
              GMB Profiles ({monthlyPlanData.gmbAccounts.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {monthlyPlanData.gmbAccounts.map((acc) => (
                <Chip
                  key={acc.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {acc.business_name || 'Business'}
                      {acc.monthly_plan_enabled && (
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                      )}
                    </Box>
                  }
                  size="small"
                  sx={{
                    bgcolor: acc.monthly_plan_enabled ? '#d1fae5' : '#f3f4f6',
                    color: acc.monthly_plan_enabled ? '#065f46' : '#6b7280',
                    fontWeight: 500
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Loading State */}
        {monthlyPlanData.loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error State */}
        {monthlyPlanData.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {monthlyPlanData.error}
          </Alert>
        )}

        {/* No Posts State */}
        {!monthlyPlanData.loading && !monthlyPlanData.error && monthlyPlanData.posts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ArticleIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No scheduled posts found for this user.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The user hasn't generated a monthly plan yet.
            </Typography>
          </Box>
        )}

        {/* Scheduled Posts Table */}
        {!monthlyPlanData.loading && monthlyPlanData.posts.length > 0 && (
          <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyPlanData.posts.map((post, index) => (
                  <TableRow key={post.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Tooltip title={post.title || post.post_name || 'Untitled'}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {post.title || post.post_name || 'Untitled'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{getPostTypeChip(post)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {formatScheduledDate(post.scheduled_time)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isPosted(post) ? (
                        <Chip
                          label="Posted"
                          size="small"
                          sx={{
                            bgcolor: '#d1fae5',
                            color: '#065f46',
                            fontWeight: 600,
                            fontSize: '11px'
                          }}
                        />
                      ) : (
                        <Chip
                          label="Scheduled"
                          size="small"
                          sx={{
                            bgcolor: '#dbeafe',
                            color: '#1e40af',
                            fontWeight: 600,
                            fontSize: '11px'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete Post">
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePost(post.id)}
                          disabled={deletingPostId === post.id || isPosted(post)}
                          sx={{
                            color: '#ef4444',
                            '&:hover': { bgcolor: '#fef2f2' }
                          }}
                        >
                          {deletingPostId === post.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary Stats */}
        {!monthlyPlanData.loading && monthlyPlanData.posts.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Total: ${monthlyPlanData.posts.length}`}
              size="small"
              sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}
            />
            <Chip
              label={`Scheduled: ${monthlyPlanData.posts.filter(p => !p.posted_at).length}`}
              size="small"
              sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }}
            />
            <Chip
              label={`Posted: ${monthlyPlanData.posts.filter(p => p.posted_at).length}`}
              size="small"
              sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
            />
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default UserView;
